'use client';

import React, { useEffect, useCallback } from 'react';
import { useFullPagerStore, parseUrlParams, restorePersistedState } from '../../store/useFullPagerStore';
import { applicationApi } from '@/modules/application/services/application.api';
import ApplicationFullPager from '@/modules/application/components/ApplicationFullPager';
import { useBodyScrollLock } from '@/shell/useBodyScrollLock';

// Ensure section registrations run before rendering
import '@/modules/cv-screening';

export default function FullPagerPortal() {
    const { isOpen, entityType, entityId, close, open, updateNavigation } = useFullPagerStore();

    // Auto-open from URL params on mount, restoring navigation context from sessionStorage
    // and re-validating against the server for freshness.
    useEffect(() => {
        const params = parseUrlParams();
        if (!params || isOpen) return;

        const persisted = restorePersistedState();
        const navCtx = persisted.navigationContext;

        if (navCtx) {
            // Re-validate: ask the server for the current total and the entity's
            // position within the same context. This ensures pagination is fresh
            // even if the list changed since the page was last open.
            open({
                entityType: params.entityType,
                entityId: params.entityId,
                section: params.section || 'summary',
                navigationContext: navCtx,
                breadcrumbs: persisted.breadcrumbs,
                taskId: params.taskId || undefined,
                scope: params.scope || undefined,
            });

            applicationApi.navigateInContext({
                jobPostingId: navCtx.jobId,
                stageId: navCtx.stageId,
                internalStatus: navCtx.internalStatus,
                targetIndex: navCtx.currentIndex,
                searchTerm: navCtx.searchTerm,
                sortField: navCtx.sortField,
                sortDirection: navCtx.sortDirection,
                filters: navCtx.filters,
            }).then(({ data }) => {
                updateNavigation({
                    total: data.totalInContext,
                    currentIndex: data.indexInContext >= 0 ? data.indexInContext : navCtx.currentIndex,
                });
            }).catch(() => {
                // Validation failed — keep stale context, better than nothing
            });
        } else {
            open({
                entityType: params.entityType,
                entityId: params.entityId,
                section: params.section || 'summary',
                taskId: params.taskId || undefined,
                scope: params.scope || undefined,
            });
        }

        // Only run on mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Close on Escape
    useEffect(() => {
        if (!isOpen) return;
        function handleKey(e: KeyboardEvent) {
            if (e.key === 'Escape') {
                const target = e.target as HTMLElement;
                if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return;
                close();
            }
        }
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [isOpen, close]);

    // Prevent body scroll when open. Ref-counted so multiple overlays across
    // multiple tabs don't fight over `document.body.style.overflow`.
    useBodyScrollLock(isOpen);

    const handleBackdropClick = useCallback((e: React.MouseEvent) => {
        if (e.target === e.currentTarget) close();
    }, [close]);

    if (!isOpen || !entityType || !entityId) return null;

    return (
        <div
            className="fixed inset-0 z-[200] flex"
            onClick={handleBackdropClick}
        >
            <div className="w-full h-full bg-white shadow-2xl flex flex-col animate-in fade-in slide-in-from-bottom-2 duration-200">
                {entityType === 'application' && (
                    <ApplicationFullPager applicationId={entityId} />
                )}
                {entityType === 'person' && (
                    <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                        Person full-pager coming soon
                    </div>
                )}
            </div>
        </div>
    );
}
